import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    getPermissionPolicies,
    getPermissionResources,
    getPermissionScopes,
} from '../../../services'
import { Permission, PolicyDetail } from '../../../types'
import { Loader2 } from 'lucide-react'
import ExpandedPolicyGroups from './expanded-policy-groups'

// Renders associated policies, resources, scopes for a permission
const ExpandedPermissionDetail: React.FC<{ row: Permission }> = ({ row }) => {
    const { t } = useTranslation('authorization')

    // Fetch associated policies
    const { data: policiesData, isLoading: loadingPolicies } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.AUTHORIZATION_PERMISSIONS,
                'policies',
                row.id,
            ],
            queryFn: () => getPermissionPolicies(row.id),
        })

    // Fetch associated resources
    const { data: resourcesData, isLoading: loadingResources } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.AUTHORIZATION_PERMISSIONS,
                'resources',
                row.id,
            ],
            queryFn: () => getPermissionResources(row.id),
        })

    // Fetch associated scopes
    const { data: scopesData, isLoading: loadingScopes } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.AUTHORIZATION_PERMISSIONS, 'scopes', row.id],
        queryFn: () => getPermissionScopes(row.id),
    })

    const policyColumns = useMemo<DataTableColumn<PolicyDetail>[]>(
        () => [
            {
                key: 'type',
                title: 'Type',
                width: 100,
                render: (value: string) => (
                    <span className="inline-flex px-1.5 py-0.5 rounded text-xs bg-orange-50 text-orange-700 border border-orange-200">
                        {value}
                    </span>
                ),
            },
            {
                key: 'name',
                title: 'Policy Name',
            },
            {
                key: 'logic',
                title: 'Logic',
                width: 100,
            },
            {
                key: 'decisionStrategy',
                title: 'Decision',
                width: 120,
            },
        ],
        []
    )

    const isLoading = loadingPolicies || loadingResources || loadingScopes

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 p-4 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t('translation:loading')}</span>
            </div>
        )
    }

    const policies = policiesData?.results || []
    const resources = resourcesData?.results || []
    const scopes = scopesData?.results || []

    return (
        <div className="p-4 space-y-3">
            {/* Description */}
            {row.description && (
                <div>
                    <span className="text-xs font-semibold text-muted-foreground">
                        Description
                    </span>
                    <p className="text-sm">{row.description}</p>
                </div>
            )}

            {/* Associated Resources */}
            <div>
                <span className="text-xs font-semibold text-muted-foreground">
                    Resources ({resources.length})
                </span>
                {resources.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mt-1">
                        {resources.map((resource) => (
                            <span
                                key={resource._id || resource.id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200"
                            >
                                {resource.name}
                                {resource.type && (
                                    <span className="text-muted-foreground">
                                        ({resource.type})
                                    </span>
                                )}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                        No resources
                    </p>
                )}
            </div>

            {/* Associated Scopes */}
            <div>
                <span className="text-xs font-semibold text-muted-foreground">
                    Scopes ({scopes.length})
                </span>
                {scopes.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mt-1">
                        {scopes.map((scope) => (
                            <span
                                key={scope.id}
                                className="inline-flex px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200"
                            >
                                {scope.name}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                        No scopes
                    </p>
                )}
            </div>

            {/* Associated Policies — table with expandable rows for groups/users */}
            <div>
                <span className="text-xs font-semibold text-muted-foreground">
                    Policies ({policies.length})
                </span>
                {policies.length > 0 ? (
                    <div className="mt-1">
                        <DataTableCustomized<PolicyDetail>
                            columns={policyColumns}
                            rows={policies}
                            rowKey={(row) => row.id}
                            emptyMessage={t(
                                'translation:noData',
                                'Không có dữ liệu'
                            )}
                            expandable={{
                                expandedRowRender: (policy) => (
                                    <ExpandedPolicyGroups policy={policy} />
                                ),
                                rowExpandable: (policy) =>
                                    ['group', 'user', 'role'].includes(
                                        policy.type
                                    ),
                            }}
                            bordered
                        />
                    </div>
                ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                        No policies
                    </p>
                )}
            </div>
        </div>
    )
}

export default ExpandedPermissionDetail
